import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { PageIntro } from "../components/site/PageIntro";
import { StoneCard } from "../components/site/StoneCard";
import { FadeUp } from "../components/site/Reveal";
import { useSeo } from "../components/site/Seo";
import { useLang } from "../i18n/lang";
import { useMeta, useStones } from "../lib/queries";

const SIZES = [150, 200, 250, 300];
const EMPTY = { q: "", category: "", form: "", color: "", finish: "", size: "", availability: "" };

const Filter = ({ label, value, onChange, options, testid }) => (
  <label className="block min-w-0">
    <span className="eyebrow !text-[0.64rem] text-[#555555]">{label}</span>
    <select className="field !py-2 text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

export default function Stones() {
  const { t, lang } = useLang();
  useSeo(...t.seo.stones);
  const { data: stones = [], isLoading } = useStones();
  const { data: meta } = useMeta();
  const [f, setF] = useState(EMPTY);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const cats = meta?.categories || [];
  const uniq = (key) => [...new Set(stones.map((s) => s[key]).filter(Boolean))];

  const list = useMemo(() => stones.filter((s) => {
    const q = f.q.trim().toLowerCase();
    if (q && ![s.code, s.name_en, s.name_it].some((x) => (x || "").toLowerCase().includes(q))) return false;
    if (f.category && s.category !== f.category) return false;
    if (f.form && s.form !== f.form) return false;
    if (f.color && s.color !== f.color) return false;
    if (f.finish && s.finish !== f.finish) return false;
    if (f.availability && s.availability !== f.availability) return false;
    if (f.size && !(Number(s.length_cm) >= Number(f.size))) return false;
    return true;
  }), [stones, f]);

  const any = [["", t.archive.any]];
  const usedCats = cats.filter((c) => uniq("category").includes(c.slug));

  return (
    <main data-testid="stones-page">
      <PageIntro n={0} label={t.archive.label} title={t.archive.title} text={t.archive.intro} testid="stones-intro" />
      <section className="px-6 sm:px-10 lg:px-14 pb-32">
        <div className="border-y border-[#1A1A1A]/15 py-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-x-6 gap-y-5 items-end" data-testid="stone-filters">
          <label className="col-span-2 relative block">
            <span className="eyebrow !text-[0.64rem] text-[#555555]">{t.archive.search}</span>
            <input className="field !py-2 text-sm pr-6" value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="SE-MB-001" data-testid="stone-search" />
            <Search className="w-4 h-4 absolute right-0 bottom-3 text-[#555555]" strokeWidth={1.25} />
          </label>
          <Filter label={t.archive.type} value={f.category} onChange={set("category")} testid="filter-type"
            options={[...any, ...usedCats.map((c) => [c.slug, lang === "it" ? c.name_it : c.name_en])]} />
          <Filter label={t.archive.form} value={f.form} onChange={set("form")} testid="filter-form"
            options={[...any, ...uniq("form").map((v) => [v, t.form[v]])]} />
          <Filter label={t.archive.color} value={f.color} onChange={set("color")} testid="filter-color"
            options={[...any, ...uniq("color").map((v) => [v, t.colors[v] || v])]} />
          <Filter label={t.archive.finish} value={f.finish} onChange={set("finish")} testid="filter-finish"
            options={[...any, ...uniq("finish").map((v) => [v, t.finishes[v] || v])]} />
          <Filter label={t.archive.size} value={f.size} onChange={set("size")} testid="filter-size"
            options={[...any, ...SIZES.filter((n) => stones.some((s) => Number(s.length_cm) >= n)).map((n) => [String(n), t.archive.sizeMin(n)])]} />
          <Filter label={t.archive.availability} value={f.availability} onChange={set("availability")} testid="filter-availability"
            options={[...any, ...uniq("availability").map((v) => [v, t.avail[v]])]} />
        </div>
        <div className="flex items-center justify-between py-6">
          <p className="f-mono text-xs text-[#555555]" data-testid="stone-count">{isLoading ? t.common.loading : t.archive.count(list.length)}</p>
          {JSON.stringify(f) !== JSON.stringify(EMPTY) && (
            <button type="button" className="eyebrow link-line" onClick={() => setF(EMPTY)} data-testid="filters-reset">{t.archive.reset}</button>
          )}
        </div>
        {!isLoading && list.length === 0 && <p className="py-24 f-serif text-3xl font-light text-[#555555]" data-testid="stones-empty">{t.archive.empty}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 mt-4">
          {list.map((s, i) => (
            <FadeUp key={s.id} delay={(i % 3) * 0.08} className={i % 3 === 1 ? "lg:mt-16" : ""}>
              <StoneCard stone={s} cats={cats} />
            </FadeUp>
          ))}
        </div>
      </section>
    </main>
  );
}
